import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components/macro';

import { classes, fontDefault, fontDefaultAlt } from '@mirinae/react-ui';

import PageElements from '@mirinae/lessons/components/lesson/PageElements';
import Element from '@mirinae/lessons/components/lesson/elements/Element';
import { FormattedText } from '@mirinae/lessons/components/lesson/elements/Text';
import { FormattedKoPhrase } from '@mirinae/lessons/components/lesson/elements/Phrase';
import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import { PageTop } from '@mirinae/lessons/components/lesson/pages/DefaultPage';
import { lessonsPalette } from '@mirinae/lessons/modules/defines/styles';

const SummaryWrapper = styled(Element)`
    display: flex;
    justify-content: center;
    min-height: min-content;
`;

const SummaryFrame = styled.div`
    // position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    border: 1px solid ${lessonsPalette.grey[5]};;
    box-sizing: border-box;
    border-radius: 10px;
    width: max-content;
    
    .${classes.mobile} & {
        margin-bottom: 20px;
        width: 100%;
    }
`;

const Title = styled.div`
    background: ${lessonsPalette.light[2]};
    border-radius: 17px;
    text-align: center;
    margin-top: -17px;
    padding: 7px 20px;
    width: fit-content;
    font-family: ${fontDefaultAlt};
    font-style: normal;
    font-weight: bold;
    font-size: 12px;
    line-height: 15px;
    color: ${lessonsPalette.grey[2]};    
`;

const Section = styled.div`
    .${classes.desktop} & {
        margin: 10px 0px 20px 0px;
        padding: 20px 20px;
    }
      
    .${classes.mobile} & {
        margin: 0px 20px 0px 20px;
        padding: 20px 0px;
        width: calc(100% - 40px);
    }
`;

const Sections = styled.div`
    display: flex;
    
    .${classes.desktop} & {
        flex-direction: row;
        
        ${Section}:not(:last-child) {
            border-right: 1px solid ${lessonsPalette.grey[5]};;
        }
    }
    
    .${classes.mobile} & {
        flex-direction: column;
        align-items: center;
        
        ${Section}:not(:last-child) {
            border-bottom: 1px solid ${lessonsPalette.grey[5]};;
        }
    }  
`;

const TextSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    
    .${classes.desktop} & {
        max-width: ${props => props.player.currentPage.lessonBounds.width * 0.25}px;
        width: ${props => props.width - 40}px; // fit-content;
    }
    
    .${classes.mobile} & {
        width: 100%;
    }
`;

const SectionTitle = styled.div`
    font-family: ${fontDefault};
    font-style: normal;
    font-weight: bold;
    font-size: 14px;
    line-height: 17px;
    text-align: center;
    color: ${lessonsPalette.darken[1]};
    padding-bottom: 20px;
`;

const SectionText = styled.div`
    font-family: ${fontDefault};
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    color: ${lessonsPalette.grey[0]};
    text-align: center;
`;

const BulletsSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    
    .${classes.desktop} & {
        max-width: ${props => props.player.currentPage.lessonBounds.width * 0.25}px;
        width: ${props => props.width - 40}px; // fit-content; 
    }
    
    .${classes.mobile} & {
        width: 100%;
    }
`;

const BulletList = styled.ul`
`;

const ListItem = styled.li`
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    color: ${lessonsPalette.grey[0]};
`;

const Combiner = styled(ListItem)`
    font-family: ${fontDefault};
`;

const ExamplesSection = styled(TextSection)`
`;

const ExampleStack = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    
    div {
        margin-bottom: 5px;
    }
`;

const Example = styled(FormattedKoPhrase)`
`;

const ExampleKOStyle = { koFontWeight: 'medium', koreanFontSize: '15px' };

// the renderer for SummaryPage, find summaryBox & section elements and manages their layout
const SummaryPage = ({ page }) => {
    const [summarySections, setSummarySections] = useState([]);
    const [summaryTitle, setSummaryTitle] = useState(null);
    const player = useContext(PlayerContext);

    const summaryElements = ['summaryBox', 'summaryTextSection', 'summaryKoBulletsSection', 'summaryEnBulletsSection', 'summaryExamplesSection'];

    useEffect(() => {
        const summarySections = [];
        page.children.forEach(step => step.children.forEach(e => {
            if (e.type === 'summaryBox') setSummaryTitle(e);
            else if (summaryElements.includes(e.type)) summarySections.push(e);
        }));
        if (summarySections.length > 0) {
            setSummarySections(summarySections.sort((a, b) => a.geom.x - b.geom.x)); // sort left-to-right
        }
    }, []);

    // render all summary-box elements found on the page in summary-box frame
    const SummaryBox = ({ element }) => setSummarySections.length > 0 && (
        <SummaryWrapper element={element}>
            <SummaryFrame>
                <Title>{summaryTitle.fieldsByName.title.value}</Title>
                <Sections>
                    { summarySections.map((section, i) => (
                        <Section key={i}>
                            { section.type === 'summaryTextSection' && (
                                <TextSection player={player} width={element.geom.width / summarySections.length}>
                                    <SectionTitle>{section.fieldsByName.sectionTitle.value}</SectionTitle>
                                    <SectionText>
                                        <FormattedText text={section.fieldsByName.sectionText.value} styles={section.fieldsByName.sectionText.styles} links={section.fieldsByName.sectionText.links} />
                                    </SectionText>
                                </TextSection>
                            )}
                            { section.type === 'summaryEnBulletsSection' && (
                                <BulletsSection player={player} width={element.geom.width / summarySections.length}>
                                    <SectionTitle>{section.fieldsByName.sectionTitle.value}</SectionTitle>
                                    <BulletList>
                                        { section.fieldsByName.bullets.parts.englishParts.map((p, i) => (
                                            <Combiner type="circle" key={i} part={p}>{p.value}</Combiner>
                                        ))}
                                    </BulletList>
                                </BulletsSection>
                            )}
                            { section.type === 'summaryKoBulletsSection' && (
                                <BulletsSection player={player} width={element.geom.width / summarySections.length}>
                                    <SectionTitle>{section.fieldsByName.sectionTitle.value}</SectionTitle>
                                    <BulletList>
                                        { section.fieldsByName.bullets.parts.koreanParts.map((p, i) => (
                                            <Combiner type="circle" key={i} part={p}>{p.value}</Combiner>
                                        ))}
                                    </BulletList>
                                </BulletsSection>
                            )}
                            { section.type === 'summaryExamplesSection' && (
                                <ExamplesSection player={player} width={element.geom.width / summarySections.length}>
                                    <SectionTitle>{section.fieldsByName.sectionTitle.value}</SectionTitle>
                                    <ExampleStack>
                                        { !section.fieldsByName.example1.hidden && (
                                            <Example phrase={section.fieldsByName.example1} phraseStyle={ExampleKOStyle} />
                                        )}
                                        { !section.fieldsByName.example2.hidden && (
                                            <Example phrase={section.fieldsByName.example2} phraseStyle={ExampleKOStyle} />
                                        )}
                                    </ExampleStack>
                                </ExamplesSection>
                            )}
                        </Section>
                    ))}
                </Sections>
            </SummaryFrame>
        </SummaryWrapper>
    );

    return (
        <>
            <PageTop page={page} />
            <PageElements page={page} except={summaryElements} mapComponent={e => e.name === 'summaryBoxLayout' && SummaryBox} />
        </>
    );
};

export default SummaryPage;
